import ReactGAImport from "react-ga4";

// react-ga4's CJS module re-exports its own `default` (the GA4 singleton) as
// `exports.default`. Depending on how a given bundle/chunk interops that CJS
// module for a default import, `ReactGAImport` can come through already
// unwrapped (has `.initialize`) or still wrapped one level deeper (only
// `.default` has `.initialize`). Resolve defensively so both shapes work.
const ReactGA: typeof ReactGAImport =
    (ReactGAImport as unknown as { initialize?: unknown }).initialize
        ? ReactGAImport
        : (ReactGAImport as unknown as { default: typeof ReactGAImport }).default;

// MBAI-500/502/517: GA4 measurement ID is now configured per environment/brand via
// VITE_GA_MEASUREMENT_ID (consistent with how other per-environment keys, e.g.
// VITE_APP_STRIPE_KEY, are supplied). When the var is unset — the default for local
// `npm run dev` — GA is never initialized and every function below is a no-op, so
// there is no tracking in local dev and no hard error either.
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

let gaInitialized = false;

// Initialize Google Analytics
export const initializeAnalytics = (): void => {
    if (!GA_MEASUREMENT_ID) {
        return;
    }
    ReactGA.initialize(GA_MEASUREMENT_ID);
    gaInitialized = true;
};

// Track Pageviews
export const trackPageview = (path: string): void => {
    if (!gaInitialized) return;
    ReactGA.send({ hitType: "pageview", page: path });
};

// Track Custom Events — GA4-native event model (event name + params object), a thin
// wrapper around react-ga4's ReactGA.event(name, params), which itself calls
// gtag('event', name, params). Safe to call from any feature entry point; a no-op
// when GA isn't initialized (see GA_MEASUREMENT_ID above).
// e.g. trackEvent('sign_up', { method: 'email' })
export const trackEvent = (eventName: string, params?: Record<string, unknown>): void => {
    if (!gaInitialized) return;
    ReactGA.event(eventName, params);
};

// MBAI-517 — named constants for the specific events tracked for that ticket, so call
// sites don't repeat raw strings. Extend this list incrementally as more feature
// entry points get instrumented (MBAI-500's broader "usage analytics" ask).
export const AnalyticsEvent = {
    // Fired once, right after a brand-new business finishes signup (email or social).
    SignUp: 'sign_up',
    // Fired when a subscription checkout session is created (payment gateway about to open).
    SubscriptionCheckoutInitiated: 'subscription_checkout_initiated',
    // Fired when a subscription payment is confirmed/activated (new plan or upgrade).
    SubscriptionConfirmed: 'subscription_confirmed',
    // Fired when the global header search actually runs a query (debounced, 3+ chars).
    // MBAI-131 re-scope: track search usage now that the search feature itself is built.
    Search: 'search',
    // MBAI-581 — remaining funnel points from spec-retention-churn.md Phase 0 (`~15 funnel points`).
    // Fired when a user creates an additional business from an already-authenticated session
    // (distinct from SignUp, which covers the business created during initial registration).
    BizCreated: 'biz_created',
    // Fired on successful contact/item/invoice creation (not edit) — activation/funnel signal.
    ContactCreated: 'contact_created',
    ItemCreated: 'item_created',
    InvoiceCreated: 'invoice_created',
    // Fired when a transaction is emailed to its contact via the send flow.
    InvoiceSent: 'invoice_sent',
    // Fired when a payment is recorded against a transaction (quick-pay or full payment form).
    PaymentRecorded: 'payment_recorded',
    // Fired on a successful (non-simulated) bank transaction import via ImportWizard.
    BankStatementImported: 'bank_statement_imported',
    // Fired when a report's "Run" action executes, across all ReportFilterPanel-based reports.
    ReportRun: 'report_run',
    // Fired when a business settings sub-page saves successfully.
    SettingsSaved: 'settings_saved',
    // Fired when the global subscription upgrade modal becomes visible.
    UpgradeModalShown: 'upgrade_modal_shown',
    // Fired when the user opens the cancel-subscription confirmation (intent, not yet confirmed).
    SubscriptionCancelInitiated: 'subscription_cancel_initiated',
    // Fired when a subscription cancellation is confirmed and processed.
    SubscriptionCancelConfirmed: 'subscription_cancel_confirmed',
} as const;
